import { SortingSpecifier, sortingMethodFromSpecifier } from '@airreading/common/data';
import { ColumnSort, OnChangeFn, SortingState, TableOptions } from '@tanstack/react-table';
import { useCallback } from 'react';

import { Accessor } from './routing';

function columnSortFromSpecifier<Field extends string>(specifier: SortingSpecifier<Field>): ColumnSort {
  const { field, order } = sortingMethodFromSpecifier(specifier);
  return { id: field, desc: order === 'desc' };
}

function toSorting<Field extends string>(sortBy: SortingSpecifier<Field>[]): SortingState {
  return sortBy.map(columnSortFromSpecifier);
}

function toSpecifiers<Field extends string>(sorting: SortingState): SortingSpecifier<Field>[] {
  return sorting.map<SortingSpecifier<Field>>(({ id, desc }) => `${id as Field}-${desc ? 'desc' : 'asc'}`);
}

export function useSorting<Field extends string>([sortBy, didChangeSortBy]: Accessor<
  SortingSpecifier<Field>[] | undefined
>) {
  const sorting = toSorting(sortBy ?? []);
  const onSortingChange = useCallback<OnChangeFn<SortingState>>(
    (sortingUpdater) => {
      const newSorting = typeof sortingUpdater === 'function' ? sortingUpdater(sorting) : sortingUpdater;
      didChangeSortBy(toSpecifiers(newSorting));
    },
    [sorting, didChangeSortBy],
  );

  return {
    enableMultiSort: true,
    isMultiSortEvent: () => true,
    enableMultiRemove: true,
    manualSorting: true,
    onSortingChange,
    sorting,
  } satisfies Partial<TableOptions<unknown>> & { sorting: SortingState };
}
